/* =========================================================
   Ankit START - DFW XLSX Export (Unified)
   File: assets/dfw-xlsx-export.js

   Purpose
   ---------------------------------------------------------
   Handles all 4 download buttons site-wide:
     - Cart page             [data-dfw-export-btn]
     - Express Order page    #dfw-download-express
     - Collection page       #dfw-download-collection
     - Reorder page          #dfw-download-reorder

   Plus the Quote file generator used by dfw-quote-submit.js
   which displays:
     - Option E header — 2 rows (navy title + tagline,
       cream contact bar with state abbreviations and
       pipe-separated phone numbers)
     - QUOTE FOR address block + CONTACT block with
       Quote ID and Generated timestamp
     - A VOLUME DISCOUNT APPLIED banner showing total
       free cases earned, dollar value, a gold SUBTOTAL pill,
       and a green "X.X% SAVED" pill
     - Mint-green row highlighting for any line that has
       qualified for a volume discount tier
     - The applied tier displayed prominently in the deals
       column (e.g. "✓ APPLIED: Buy 15 → Get 2 Free")

   All generate a branded .xlsx file with:
     - DFW Motel Supply header + contact info
     - Product images embedded (no internet needed to view)
     - Relevant columns per page
     - Frozen header row

   Dependencies
   ---------------------------------------------------------
   - ExcelJS 4.x loaded from CDN on first click (cached)
   - window.DFWBXGY_REGISTRY for cart BXGY deals
   - data-* attributes on product row elements (see below)

   Cart rows          [data-item-row]
     data-export-title, data-export-sku, data-export-qty,
     data-export-unit-price, data-export-line-price,
     data-export-image, data-export-url, data-bxgy,
     data-product-id

   Express/Collection rows  .dfw-export-item
     data-title, data-sku, data-price, data-image,
     data-boxqty, data-unit

   Reorder rows       .dfw-export-item
     data-title, data-sku, data-price, data-image,
     data-boxqty, data-unit, data-order-qty

   ========================================================= */

(function () {

  'use strict';

  var EXCELJS_CDN = 'https://cdn.jsdelivr.net/npm/exceljs@4.4.0/dist/exceljs.min.js';

  /* =========================================================
     ExcelJS — load once, shared across all pages
     ========================================================= */

  function loadExcelJs() {
    return new Promise(function (resolve, reject) {
      if (window.ExcelJS) { resolve(); return; }
      var script = document.createElement('script');
      script.src = EXCELJS_CDN;
      script.onload = function () { resolve(); };
      script.onerror = function () {
        reject(new Error('Could not load ExcelJS. Check your internet connection.'));
      };
      document.head.appendChild(script);
    });
  }

  /* =========================================================
     Image fetch → base64
     ========================================================= */

  function fetchImg(url) {
    if (!url) return Promise.resolve(null);
    return fetch(url)
      .then(function (res) { return res.ok ? res.blob() : null; })
      .then(function (blob) {
        if (!blob) return null;
        return new Promise(function (resolve) {
          var r = new FileReader();
          r.onloadend = function () {
            var result = r.result || '';
            resolve(result.split(',')[1] || null);
          };
          r.onerror = function () { resolve(null); };
          r.readAsDataURL(blob);
        });
      })
      .catch(function () { return null; });
  }

  /* =========================================================
     Helpers
     ========================================================= */

  function dateStamp() {
    var d = new Date();
    return d.getFullYear() +
      String(d.getMonth() + 1).padStart(2, '0') +
      String(d.getDate()).padStart(2, '0');
  }

  function setLoading(el, loading) {
    if (!el) return;
    if (loading) {
      el._origHTML = el.innerHTML;
      el.innerHTML = 'Building\u2026';
      el.disabled = true;
      el.style.opacity = '0.65';
    } else {
      el.innerHTML = el._origHTML || 'Download';
      el.disabled = false;
      el.style.opacity = '1';
    }
  }

  function cleanPrice(val) {
    if (val === undefined || val === null || val === '') return 0;
    return parseFloat(String(val).replace(/[^0-9.]/g, '')) || 0;
  }

  /* Format a number as "$X,XXX.XX" with thousands separators.
     Used by the Volume Discount banner pills so a $20,990
     subtotal reads cleanly with commas, not "$20990.00".
     toLocaleString handles the comma insertion + 2 decimals. */
  function formatMoney(val) {
    var num = (typeof val === 'number') ? val : cleanPrice(val);
    return '$' + num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function imgColIndex(columns) {
    for (var i = 0; i < columns.length; i++) {
      if (columns[i].key === 'image') return i;
    }
    return -1;
  }

  /* Look for total:true column first (where ORDER TOTAL label ends).
     Falls back to numFmt for safety with older callers. */
  function firstNumericColIndex(columns) {
    for (var i = 0; i < columns.length; i++) {
      if (columns[i].total) return i;
    }
    for (var i = 0; i < columns.length; i++) {
      if (columns[i].numFmt) return i;
    }
    return columns.length - 1;
  }

  /* =========================================================
     BXGY deal helpers
     =========================================================
     getDealsInfo(productId, bxgyRaw)
       Returns sorted array of { buy, free } tiers.
       Used by both the display string (getDeals) and the
       earned-tier calculation (getEarnedTier) so the parse
       logic lives in exactly one place.

     getEarnedTier(tiers, qty)
       Returns the highest tier the qty has qualified for,
       or null if none. Semantics: HIGHEST-TIER-WINS, not
       multiples. Qty 17 with tiers [Buy 5→1, Buy 15→2,
       Buy 25→3] returns {buy:15, free:2}.

     getDeals(productId, bxgyRaw)
       Backwards-compatible display string used by cart,
       express, collection, and reorder paths. Unchanged
       behavior — just delegates parsing to getDealsInfo.
     ========================================================= */

  function getDealsInfo(productId, bxgyRaw) {
    var tiers = [];

    var registry = window.DFWBXGY_REGISTRY || {};
    var regTiers = registry[String(productId)];
    if (regTiers && regTiers.length) {
      tiers = regTiers.map(function (t) {
        return { buy: Number(t.buy_qty), free: Number(t.free_qty) };
      });
    }

    if (!tiers.length && bxgyRaw) {
      try {
        var parsed = typeof bxgyRaw === 'string' ? JSON.parse(bxgyRaw) : bxgyRaw;
        var arr = Array.isArray(parsed) ? parsed : (parsed.deals || parsed.tiers || []);
        arr.forEach(function (d) {
          var buy  = Number(d.buy_qty || d.buy_quantity || d.buy || 0);
          var free = Number(d.free_qty || d.get_quantity || d.free || d.get || 0);
          if (buy && free) tiers.push({ buy: buy, free: free });
        });
      } catch (e) {}
    }

    tiers.sort(function (a, b) { return a.buy - b.buy; });
    return tiers;
  }

  function getEarnedTier(tiers, qty) {
    if (!tiers || !tiers.length) return null;
    var earned = null;
    for (var i = 0; i < tiers.length; i++) {
      if (qty >= tiers[i].buy) earned = tiers[i];
      else break;
    }
    return earned;
  }

  function getDeals(productId, bxgyRaw) {
    var tiers = getDealsInfo(productId, bxgyRaw);
    if (!tiers.length) return '';
    return tiers.map(function (t) {
      return 'Buy ' + t.buy + ' \u2192 Get ' + t.free + ' Free';
    }).join('  |  ');
  }

  /* Combined helper: given a productId, raw bxgy data, and the
     qty in cart, returns:

       tiers      — sorted array of {buy, free}
       earned     — {buy, free} of the highest qualifying tier (or null)
       multiplier — how many times the earned tier "stacks" in qty
       freeQty    — TOTAL free units earned (multiplier × earned.free)
       dealsStr   — display string for the Volume Discount column

     Math mirrors dfw-bxgy-engine.js exactly:
         multiplier = floor(qty / earned.buy)
         freeQty    = multiplier × earned.free
     So qty 50 with tier Buy 25 → Get 2 returns freeQty = 4 (not 2).
     Qty 30 with tier Buy 5 → Get 1 returns freeQty = 6.

     dealsStr formatting (customer-facing language):
       mult = 1 :  "✓ APPLIED: Buy 5 → Get 1 Free"
       mult > 1 :  "✓ APPLIED: Buy 25 → Get 2 Free  (×2 = 4 Free)"
       no apply :  "Buy 5 → Get 1 Free  |  Buy 25 → Get 2 Free"
       no tiers :  ""

     "✓ APPLIED" matches the top banner's "VOLUME DISCOUNT APPLIED"
     wording so the customer's eye links the row treatment to the
     headline claim. Used by both runCart and runQuoteFile so the
     formatting stays identical across the cart and quote files. */
  function buildDealsForItem(productId, bxgyRaw, qty) {
    var tiers  = getDealsInfo(productId, bxgyRaw);
    var earned = getEarnedTier(tiers, qty);

    var multiplier = 0;
    var freeQty    = 0;
    if (earned && earned.buy > 0) {
      multiplier = Math.floor(qty / earned.buy);
      freeQty    = multiplier * earned.free;
    }

    var dealsStr = '';
    if (earned && freeQty > 0) {
      if (multiplier > 1) {
        dealsStr = '\u2713 APPLIED: Buy ' + earned.buy +
                   ' \u2192 Get ' + earned.free + ' Free  ' +
                   '(\u00D7' + multiplier + ' = ' + freeQty + ' Free)';
      } else {
        dealsStr = '\u2713 APPLIED: Buy ' + earned.buy +
                   ' \u2192 Get ' + earned.free + ' Free';
      }
    } else if (tiers.length) {
      dealsStr = tiers.map(function (t) {
        return 'Buy ' + t.buy + ' \u2192 Get ' + t.free + ' Free';
      }).join('  |  ');
    }

    return {
      tiers:      tiers,
      earned:     earned,
      multiplier: multiplier,
      freeQty:    freeQty,
      dealsStr:   dealsStr
    };
  }

  /* =========================================================
     Core XLSX builder
     =========================================================
     opts {
       items         []     — array of plain objects
       columns       []     — column definitions (see below)
       imageDataList []     — base64 strings, same order as items
       filename      str
       sheetName     str
       sheetTitle    str    — shown in generated-date row
       showTotals    bool   — show totals row (cart only)
       note          str    — footer note text
     }

     Column definition {
       key        str   — property name on item
       header     str   — header label
       width      num   — column width (characters)
       align      str   — 'left' | 'center' | 'right'
       numFmt     str   — Excel number format string (makes it numeric)
       bold       bool
       color      str   — ARGB hex e.g. 'FF444444'
       wrap       bool  — wrapText
       font       {}    — extra ExcelJS font overrides
       total      bool  — include this column in the totals row
     }
     ========================================================= */

  function buildXLSX(opts) {
    var wb = new window.ExcelJS.Workbook();
    wb.creator  = 'GoWithDFW.com';
    wb.created  = new Date();
    wb.modified = new Date();

    var ws = wb.addWorksheet(opts.sheetName || 'Sheet1', {
      pageSetup: {
        paperSize   : 9,
        orientation : 'landscape',
        fitToPage   : true,
        fitToWidth  : 1,
        fitToHeight : 0
      }
    });

    var cols = opts.columns;
    var N    = cols.length; // total number of columns

    ws.columns = cols.map(function (c) {
      return { key: c.key, width: c.width };
    });

    /* --------------------------------------------------
       Option E Header (rows 1-2 only — was 5 rows)
       --------------------------------------------------
       Row 1 — Navy title "DFW Motel Supply" + tagline below
       Row 2 — Cream contact bar: phones left, website+email right

       Trims 3 rows off the previous layout. The website / email
       row that used to sit on navy is now consolidated onto the
       cream bar with the phone numbers. The DFW gold borders
       still frame the cream bar so the brand color story stays. */

    function mergeRow(row, value, fontOpts, fillArgb, alignH) {
      ws.mergeCells(row, 1, row, N);
      var cell = ws.getRow(row).getCell(1);
      cell.value = value;
      cell.font  = Object.assign({ name: 'Calibri' }, fontOpts);
      if (fillArgb) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } };
      cell.alignment = { vertical: 'middle', horizontal: alignH || 'center' };
      return cell;
    }

    /* Row 1 — Title row with brand name (centered on navy).
       Tagline removed per Ankit's request — keeps the title
       cleaner and gives the brand name full presence. */
    ws.mergeCells(1, 1, 1, N);
    var titleCell = ws.getRow(1).getCell(1);
    titleCell.value = 'DFW Motel Supply';
    titleCell.font  = { name: 'Calibri', bold: true, size: 22, color: { argb: 'FFFFFFFF' } };
    titleCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A2E' } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    ws.getRow(1).height = 42;   /* shorter without the tagline line */

    /* Row 2 — Cream contact bar split 60/40.
       Excel can't do a single cell with internal left+right text
       on different alignments, so we split the row at column N-1:
         cols 1..floor(N*0.6)        → phones (left-aligned)
         cols floor(N*0.6)+1..N      → website+email (right-aligned)
       For 7-col layouts (cart/quote) the split is roughly cols 1-4
       on the left and cols 5-7 on the right — plenty of room. */
    var creamSplit = Math.max(1, Math.floor(N * 0.6));

    /* LEFT half — phones */
    if (creamSplit > 1) {
      ws.mergeCells(2, 1, 2, creamSplit);
    }
    var phonesCell = ws.getRow(2).getCell(1);
    phonesCell.value = {
      richText: [
        { text: 'TX (214) 745-8866   ',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF5A3E00' } } },
        { text: '|',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FFC07800' } } },
        { text: '   CA (951) 788-0000   ',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF5A3E00' } } },
        { text: '|',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FFC07800' } } },
        { text: '   NJ (856) 423-3400',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF5A3E00' } } }
      ]
    };
    phonesCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7E0' } };
    phonesCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    phonesCell.border = {
      top:    { style: 'thin', color: { argb: 'FFE8A000' } },
      bottom: { style: 'thin', color: { argb: 'FFE8A000' } }
    };

    /* RIGHT half — website + email */
    if (creamSplit < N) {
      ws.mergeCells(2, creamSplit + 1, 2, N);
    }
    var webEmailCell = ws.getRow(2).getCell(creamSplit + 1);
    webEmailCell.value = {
      richText: [
        { text: 'www.gowithdfw.com  ',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF5A3E00' } } },
        { text: '\u00B7',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FFC07800' } } },
        { text: '  sales@dfwmotelsupply.com',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF5A3E00' } } }
      ]
    };
    webEmailCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7E0' } };
    webEmailCell.alignment = { vertical: 'middle', horizontal: 'right', indent: 1 };
    webEmailCell.border = {
      top:    { style: 'thin', color: { argb: 'FFE8A000' } },
      bottom: { style: 'thin', color: { argb: 'FFE8A000' } }
    };

    ws.getRow(2).height = 22;

    /* --------------------------------------------------
       BXGY tally — done up front to decide whether to
       insert a summary banner above the column headers.
       Only triggers when the caller passes showBxgySummary
       AND at least one line has earnedFree > 0. Other
       callers (express, collection, reorder) don't compute
       earnedFree so this is a clean no-op for them.

       Also tallies subtotal so the gold SUBTOTAL pill in
       the banner can display the order total alongside the
       savings percentage.
       -------------------------------------------------- */
    var totalFreeBoxes = 0;
    var totalFreeValue = 0;
    var orderSubtotal  = 0;
    opts.items.forEach(function (it) {
      if (!it._groupHeader) {
        totalFreeBoxes += (it.earnedFree || 0);
        totalFreeValue += (it.freeValue  || 0);
        orderSubtotal  += (it.linePrice  || 0);
      }
    });
    var showBxgyBanner = !!opts.showBxgySummary && totalFreeBoxes > 0;
    var savingsPct     = (showBxgyBanner && orderSubtotal > 0)
                       ? (totalFreeValue / orderSubtotal) * 100
                       : 0;

    /* --------------------------------------------------
       Row 3 (conditional) — VOLUME DISCOUNT BANNER
       --------------------------------------------------
       Three sections in one mint row:
         Left  (cols 1..N-2)    text: deal info
         Mid   (col N-1)        gold pill: "SUBTOTAL $X,XXX.XX"
         Right (col N)          green pill: "X.X% SAVED"

       The gold subtotal pill at the top previews the gold
       ORDER TOTAL at the bottom of the file — color story
       bookends the whole document. The green pill is the
       headline savings number.

       For 7-column quote/cart layouts this puts the deal
       text in cols 1-5, subtotal pill in col 6, saved pill
       in col 7. Pills are single cells (no merge) which
       keeps the row clean and gives Excel proper boundaries
       for the colored fills. */
    var bxgyRow = 0;   /* 0 = no banner inserted */
    if (showBxgyBanner) {
      bxgyRow = 3;

      /* --------------------------------------------------
         Banner column allocation (for 7-col layout):
           cols 1-4  → deal text (left)
           cols 5-6  → gold SUBTOTAL pill (merged, gives the
                       dollar amount room — was clipping
                       at $20,990 in one cell)
           col  7    → green % SAVED pill
         For wider/narrower layouts the math adapts:
           text  = cols 1 to N-3
           subtl = cols N-2 to N-1 (merged)
           saved = col N
         -------------------------------------------------- */

      /* Left section — banner text */
      var bannerLeftEnd = Math.max(1, N - 3);
      if (bannerLeftEnd > 1) {
        ws.mergeCells(bxgyRow, 1, bxgyRow, bannerLeftEnd);
      }
      var bxgyCell = ws.getRow(bxgyRow).getCell(1);
      bxgyCell.value = {
        richText: [
          { text: '\uD83C\uDF81  VOLUME DISCOUNT APPLIED:   ',
            font: { name: 'Calibri', bold: true, size: 12, color: { argb: 'FF1F6B2C' } } },
          { text: totalFreeBoxes + (totalFreeBoxes === 1 ? ' free case' : ' free cases'),
            font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF1A1A2E' } } },
          { text: '      |      Value:  ',
            font: { name: 'Calibri', size: 11, color: { argb: 'FF1F6B2C' } } },
          { text: formatMoney(totalFreeValue),
            font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF1A1A2E' } } }
        ]
      };
      bxgyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD4F4DD' } };
      bxgyCell.alignment = { vertical: 'middle', horizontal: 'center' };
      bxgyCell.border = {
        top:    { style: 'medium', color: { argb: 'FF6FBF7C' } },
        bottom: { style: 'medium', color: { argb: 'FF6FBF7C' } }
      };

      /* Middle — gold SUBTOTAL pill, merged across 2 cells.
         Single-cell pill was clipping at $20,990. Two cells
         comfortably fits "SUBTOTAL $XXX,XXX.XX" up to 7 digits.
         Number formatted with comma thousands separator. */
      if (N >= 3) {
        ws.mergeCells(bxgyRow, N - 2, bxgyRow, N - 1);
      }
      var subtotalPill = ws.getRow(bxgyRow).getCell(Math.max(1, N - 2));
      subtotalPill.value     = 'SUBTOTAL  ' + formatMoney(orderSubtotal);
      subtotalPill.font      = { name: 'Calibri', bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      subtotalPill.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8A000' } };
      subtotalPill.alignment = { vertical: 'middle', horizontal: 'center' };
      subtotalPill.border = {
        top:    { style: 'medium', color: { argb: 'FF6FBF7C' } },
        bottom: { style: 'medium', color: { argb: 'FF6FBF7C' } }
      };

      /* Right — green percentage SAVED pill (col N) */
      var savedPill = ws.getRow(bxgyRow).getCell(N);
      savedPill.value     = savingsPct.toFixed(1) + '% SAVED';
      savedPill.font      = { name: 'Calibri', bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      savedPill.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F6B2C' } };
      savedPill.alignment = { vertical: 'middle', horizontal: 'center' };
      savedPill.border = {
        top:    { style: 'medium', color: { argb: 'FF6FBF7C' } },
        bottom: { style: 'medium', color: { argb: 'FF6FBF7C' } }
      };

      ws.getRow(bxgyRow).height = 28;
    }

    /* --------------------------------------------------
       Column headers (frozen) — row 3 (no banner) or row 4 (with banner)
       --------------------------------------------------
       Option E removed the multi-row header (was 5 rows, now 2),
       and the Savings Summary block is gone, so the data table
       starts much higher in the file. */
    var HEADER_ROW = showBxgyBanner ? 4 : 3;
    cols.forEach(function (col, i) {
      var cell = ws.getRow(HEADER_ROW).getCell(i + 1);
      cell.value = col.header;
      cell.font  = { name: 'Calibri', bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
      cell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2D2D4E' } };
      cell.alignment = { vertical: 'middle', horizontal: col.align || 'left' };
    });
    ws.getRow(HEADER_ROW).height = 22;

    /* --------------------------------------------------
       Data rows — start right after the column header row
       -------------------------------------------------- */
    var DATA_START = HEADER_ROW + 1;
    var ROW_HEIGHT = 50;
    var IMG_PX     = 50;
    var imgCol     = imgColIndex(cols);
    var totals     = {};

    /* Track real row number separately — group headers take up
       a row but don't count toward the alternating stripe index */
    var currentRow  = DATA_START;
    var stripeIndex = 0;

    opts.items.forEach(function (item, idx) {
      var rowNum = currentRow;
      currentRow++;
      var row    = ws.getRow(rowNum);

      /* --- Group header row --- */
      if (item._groupHeader) {
        row.height = 24;
        ws.mergeCells(rowNum, 1, rowNum, N);
        var hc = row.getCell(1);
        hc.value = item._groupTitle || '';
        hc.font  = { name: 'Calibri', bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
        hc.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8A000' } };
        hc.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
        hc.border = {
          top    : { style: 'thin', color: { argb: 'FFC07800' } },
          bottom : { style: 'thin', color: { argb: 'FFC07800' } }
        };
        return; /* skip normal cell rendering for this row */
      }

      row.height = ROW_HEIGHT;

      /* BXGY: lines that earned a tier get mint-green background.
         Non-earning lines fall back to the alternating gray/white
         stripe. stripeIndex only advances on non-mint rows so the
         stripe reads cleanly when mint rows are interleaved. */
      var earnedThisRow = (item.earnedFree || 0) > 0;
      var bg;
      if (earnedThisRow) {
        bg = 'FFD4F4DD';   /* mint */
      } else {
        bg = stripeIndex % 2 === 0 ? 'FFFAFAFA' : 'FFFFFFFF';
        stripeIndex++;
      }

      cols.forEach(function (col, colIdx) {
        var c    = row.getCell(colIdx + 1);
        var val  = item[col.key];
        var font = Object.assign({ name: 'Calibri', size: 10 }, col.font || {});
        if (col.bold)  font.bold  = true;
        if (col.color) font.color = { argb: col.color };

        if (col.key === 'image') {
          c.value = '';

        } else if (col.key === 'title') {
          c.value = item.url
            ? { text: String(val || ''), hyperlink: item.url }
            : String(val || '');
          font.bold  = true;
          font.color = { argb: 'FF1A1A2E' };

        } else if (col.key === 'deals') {
          c.value = val || '';
          font.size  = 9;
          /* On earned rows the deals text becomes bold dark green
             so "✓ EARNED" pops against the mint background.
             Non-earned rows keep the existing brown-orange tone. */
          if (earnedThisRow) {
            font.bold  = true;
            font.color = { argb: 'FF1F6B2C' };
          } else {
            font.color = { argb: val ? 'FF9A6000' : 'FFAAAAAA' };
          }

        } else if (col.numFmt) {
          var num = (typeof val === 'number') ? val : cleanPrice(val);
          c.value  = num;
          c.numFmt = col.numFmt;
          /* Only sum columns marked total:true */
          if (col.total) {
            totals[col.key] = (totals[col.key] || 0) + num;
          }

        } else {
          c.value = (val !== undefined && val !== null) ? val : '';
        }

        c.font      = font;
        c.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
        c.border    = { bottom: { style: 'thin', color: { argb: 'FFE5E5E5' } } };
        c.alignment = {
          vertical   : 'middle',
          horizontal : col.align || 'left',
          wrapText   : col.wrap || false
        };
        if (col.numFmt && col.key !== 'deals') c.numFmt = col.numFmt;
      });

      /* Embed image */
      var b64 = opts.imageDataList && opts.imageDataList[idx];
      if (b64 && imgCol >= 0) {
        try {
          var imgId = wb.addImage({ base64: b64, extension: 'jpeg' });
          ws.addImage(imgId, {
            tl     : { col: imgCol, row: rowNum - 1, nativeColOff: 90000 * 2, nativeRowOff: 90000 * 2 },
            ext    : { width: IMG_PX, height: IMG_PX },
            editAs : 'oneCell'
          });
        } catch (e) {}
      }
    });

    /* --------------------------------------------------
       Totals row (cart only — opts.showTotals)
       -------------------------------------------------- */
    var nextRow = currentRow; /* currentRow already advanced past last data row */

    if (opts.showTotals) {
      ws.getRow(nextRow).height = 26;
      var labelEnd = firstNumericColIndex(cols); /* cols before first total:true */

      if (labelEnd > 0) {
        ws.mergeCells(nextRow, 1, nextRow, labelEnd);
      }
      var tlLabel = ws.getRow(nextRow).getCell(1);
      tlLabel.value     = 'ORDER TOTAL';
      tlLabel.font      = { name: 'Calibri', bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      tlLabel.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8A000' } };
      tlLabel.alignment = { vertical: 'middle', horizontal: 'right' };

      cols.forEach(function (col, colIdx) {
        if (colIdx < labelEnd) return;
        var tc = ws.getRow(nextRow).getCell(colIdx + 1);
        if (totals[col.key] !== undefined) {
          tc.value  = totals[col.key];
          tc.numFmt = col.numFmt;
        } else {
          tc.value = '';
        }
        tc.font      = { name: 'Calibri', bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        tc.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8A000' } };
        tc.alignment = { vertical: 'middle', horizontal: col.align || 'right' };
      });

      nextRow++;
    }

    /* --------------------------------------------------
       Note row
       -------------------------------------------------- */
    ws.mergeCells(nextRow, 1, nextRow, N);
    var noteCell = ws.getRow(nextRow).getCell(1);
    noteCell.value = opts.note ||
      'Volume discount free cases are processed at order confirmation. Prices shown may exclude applied discounts.';
    noteCell.font      = { name: 'Calibri', size: 8, italic: true, color: { argb: 'FF999999' } };
    noteCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(nextRow).height = 16;

    /* --------------------------------------------------
       Freeze header row
       -------------------------------------------------- */
    ws.views = [{ state: 'frozen', ySplit: HEADER_ROW, activeCell: 'B' + DATA_START }];

    /* --------------------------------------------------
       Write buffer and trigger download
       -------------------------------------------------- */
    return wb.xlsx.writeBuffer().then(function (buffer) {
      var blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      var url = URL.createObjectURL(blob);
      var a   = document.createElement('a');
      a.href        = url;
      a.download    = opts.filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(url); }, 2000);
    });
  }

  /* =========================================================
     Page adapters
     ========================================================= */

  /* --- Cart --- */
  function runCart(btnEl) {

    /* Build a map of productId -> bxgy raw data from DOM.
       This is the only thing we still need from the DOM —
       everything else (qty, price, title, image) comes from
       /cart.js so it is always live after quantity changes. */
    var bxgyMap = {};
    document.querySelectorAll('[data-item-row]').forEach(function (row) {
      var pid = row.dataset.productId || '';
      if (pid && row.dataset.bxgy) bxgyMap[pid] = row.dataset.bxgy;
    });

    if (!window.DFWBXGY_REGISTRY && window.DFWBXGY_REGISTRY_API) {
      window.DFWBXGY_REGISTRY_API.rebuild();
    }

    /* Only `linePrice` carries total:true so only Line Total is summed.
       Unit Price still formats as currency but is NOT included in ORDER TOTAL. */
    var columns = [
      { key: 'image',     header: 'Image',           width: 16, align: 'center' },
      { key: 'title',     header: 'Product Name',    width: 38, align: 'left' },
      { key: 'sku',       header: 'SKU',             width: 20, align: 'left',   font: { size: 9 }, color: 'FF666666' },
      { key: 'qty',       header: 'Qty',             width: 8,  align: 'center', bold: true, color: 'FF1A1A2E', font: { size: 11, bold: true } },
      { key: 'unitPrice', header: 'Unit Price',      width: 13, align: 'right',  numFmt: '"$"#,##0.00' },
      { key: 'linePrice', header: 'Line Total',      width: 13, align: 'right',  numFmt: '"$"#,##0.00', bold: true, color: 'FF1A1A2E', total: true },
      { key: 'deals',     header: 'Volume Discount', width: 46, align: 'center', wrap: true }
    ];

    setLoading(btnEl, true);

    /* Fetch live cart data first — this reflects any qty changes
       made since the page loaded, no reload required */
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          alert('Your cart is empty.');
          setLoading(btnEl, false);
          return Promise.reject('empty');
        }

        var items = cart.items.map(function (item) {
          var pid      = String(item.product_id);
          var imageUrl = '';
          if (item.image) {
            /* Shopify CDN URLs look like:
               //cdn.shopify.com/s/files/.../image.jpg?v=123456
               Strip the query string first, then append via Shopify's
               width param — works for all Shopify CDN image formats */
            var imgBase = item.image.split('?')[0];
            imageUrl = (imgBase.startsWith('//') ? 'https:' + imgBase : imgBase) + '?width=100';
          }

          /* BXGY: structured tiers + earned-tier calculation.
             freeQty already includes the multiplier so the banner
             tally and per-row value are correct for qty > tier.buy. */
          var bxgyInfo  = buildDealsForItem(pid, bxgyMap[pid] || '', item.quantity);
          var unitPrice = item.price / 100;

          return {
            productId : pid,
            title     : item.product_title || item.title || '',
            sku       : item.sku           || '',
            qty       : item.quantity,
            unitPrice : unitPrice,
            linePrice : item.line_price    / 100,
            imageUrl  : imageUrl,
            url       : 'https://gowithdfw.com' + (item.url || ''),
            deals     : bxgyInfo.dealsStr,
            /* BXGY computed fields read by buildXLSX */
            earnedFree: bxgyInfo.freeQty,            /* multiplier already applied */
            earnedTier: bxgyInfo.earned,
            multiplier: bxgyInfo.multiplier,
            freeValue : bxgyInfo.freeQty * unitPrice
          };
        });

        return loadExcelJs()
          .then(function () {
            return Promise.all(items.map(function (item) { return fetchImg(item.imageUrl); }));
          })
          .then(function (imgs) {
            return buildXLSX({
              items           : items,
              columns         : columns,
              imageDataList   : imgs,
              filename        : 'DFW-Order-' + dateStamp() + '.xlsx',
              sheetName       : 'Cart Order',
              sheetTitle      : 'Cart Order',
              showTotals      : true,
              showBxgySummary : true,
              note            : 'Volume discount free cases are processed at order confirmation. Prices shown may exclude applied discounts.'
            });
          });
      })
      .then(function () { setLoading(btnEl, false); })
      .catch(function (err) {
        setLoading(btnEl, false);
        if (err !== 'empty') alert('Export failed: ' + (err && err.message || 'Unknown error'));
      });
  }

  /* --- Express Order & Collection (share same column set) --- */
  function runExpress(btnEl) {

    /* Collect items grouped by category heading.
       Each .dfwexpress-collection-block contains:
         h2.dfw-express-title  — the category label
         .dfw-lazy-target      — products already scrolled into view
         .dfw-lazy-template    — products not yet loaded (still in <script> template)
       We handle both so the export always includes every product
       regardless of how far the user has scrolled. */

    var parser   = new DOMParser();
    var items    = [];
    var blocks   = document.querySelectorAll('.dfwexpress-collection-block');

    if (blocks.length) {

      blocks.forEach(function (block) {
        var heading = block.querySelector('.dfw-express-title');
        var label   = heading ? heading.textContent.trim() : '';

        /* Products already in DOM (scrolled into view) */
        var loaded = block.querySelectorAll('.dfw-lazy-target .dfw-export-item');

        /* Products still in lazy template (not yet scrolled to) */
        var tpl     = block.querySelector('.dfw-lazy-template');
        var unloaded = [];
        if (tpl) {
          var doc = parser.parseFromString(tpl.innerHTML, 'text/html');
          unloaded = doc.querySelectorAll('.dfw-export-item');
        }

        var blockItems = Array.prototype.slice.call(loaded)
          .concat(Array.prototype.slice.call(unloaded));

        if (!blockItems.length) return;

        /* Insert a group header pseudo-item before each category */
        items.push({ _groupHeader: true, _groupTitle: label });

        blockItems.forEach(function (row) {
          /* data-product-id is "product-12345" — strip prefix for registry lookup */
          var rawId = (row.dataset.productId || '').replace(/^product-/, '');
          items.push({
            title    : row.dataset.title   || '',
            sku      : row.dataset.sku     || '',
            price    : cleanPrice(row.dataset.price),
            boxqty   : row.dataset.boxqty  || '',
            unit     : row.dataset.unit    || '',
            imageUrl : row.dataset.image   || '',
            url      : row.dataset.url ? 'https://gowithdfw.com' + row.dataset.url : '',
            deals    : getDeals(rawId, row.dataset.bxgy || '')
          });
        });
      });

    } else {
      /* Fallback for collection page — no blocks, flat list */
      var rows = document.querySelectorAll('.dfw-export-item');
      if (!rows.length) { alert('No products loaded yet.'); return; }
      rows.forEach(function (row) {
        var rawId = (row.dataset.productId || '').replace(/^product-/, '');
        items.push({
          title    : row.dataset.title   || '',
          sku      : row.dataset.sku     || '',
          price    : cleanPrice(row.dataset.price),
          boxqty   : row.dataset.boxqty  || '',
          unit     : row.dataset.unit    || '',
          imageUrl : row.dataset.image   || '',
          url      : row.dataset.url ? 'https://gowithdfw.com' + row.dataset.url : '',
          deals    : getDeals(rawId, row.dataset.bxgy || '')
        });
      });
    }

    if (!items.length) { alert('No products found.'); return; }

    var columns = [
      { key: 'image',  header: 'Image',        width: 16, align: 'center' },
      { key: 'title',  header: 'Product Name',  width: 42, align: 'left' },
      { key: 'sku',    header: 'SKU',           width: 22, align: 'left',   font: { size: 9 }, color: 'FF666666' },
      { key: 'price',  header: 'Price / Case',  width: 14, align: 'right',  numFmt: '"$"#,##0.00' },
      { key: 'boxqty', header: 'Box Qty',       width: 10, align: 'center', color: 'FF1A1A2E' },
      { key: 'unit',   header: 'Unit',          width: 14, align: 'center', font: { size: 9 }, color: 'FF888888' },
      { key: 'deals',  header: 'Volume Discount', width: 46, align: 'center', wrap: true }
    ];

    setLoading(btnEl, true);
    loadExcelJs()
      .then(function () {
        /* Only fetch images for real product rows, not group headers */
        return Promise.all(items.map(function (item) {
          return item._groupHeader ? Promise.resolve(null) : fetchImg(item.imageUrl);
        }));
      })
      .then(function (imgs) {
        return buildXLSX({
          items         : items,
          columns       : columns,
          imageDataList : imgs,
          filename      : 'DFW-Products-' + dateStamp() + '.xlsx',
          sheetName     : 'Product List',
          sheetTitle    : 'Product List',
          showTotals    : false,
          note          : 'Prices shown are per case. Contact us to place your order.'
        });
      })
      .then(function () { setLoading(btnEl, false); })
      .catch(function (err) {
        setLoading(btnEl, false);
        alert('Export failed: ' + (err && err.message || 'Unknown error'));
      });
  }

  /* --- Reorder --- */
  function runReorder(btnEl) {
    var rows = document.querySelectorAll('.dfw-export-item');
    if (!rows.length) { alert('No products available.'); return; }

    var items = [];
    rows.forEach(function (row) {
      items.push({
        title    : row.dataset.title    || '',
        sku      : row.dataset.sku      || '',
        price    : cleanPrice(row.dataset.price),
        boxqty   : row.dataset.boxqty   || '',
        unit     : row.dataset.unit     || '',
        lastQty  : row.dataset.orderQty || '',
        orderDate: row.dataset.orderDateHuman || '',
        deals    : getDeals(row.dataset.productId || '', row.dataset.bxgy || ''),
        imageUrl : row.dataset.image    || '',
        url      : row.dataset.url ? 'https://gowithdfw.com' + row.dataset.url : ''
      });
    });

    var columns = [
      { key: 'image',   header: 'Image',          width: 16, align: 'center' },
      { key: 'title',   header: 'Product Name',    width: 42, align: 'left' },
      { key: 'sku',     header: 'SKU',             width: 22, align: 'left',   font: { size: 9 }, color: 'FF666666' },
      { key: 'price',   header: 'Price / Case',    width: 14, align: 'right',  numFmt: '"$"#,##0.00' },
      { key: 'boxqty',  header: 'Box Qty',         width: 10, align: 'center', color: 'FF1A1A2E' },
      { key: 'unit',      header: 'Unit',            width: 14, align: 'center', font: { size: 9,  bold: true }, color: 'FF888888' },
      { key: 'lastQty',   header: 'Last Order Qty',  width: 15, align: 'center', font: { size: 10, bold: true }, color: 'FFAAAAAA' },
      { key: 'orderDate', header: 'Last Order Date', width: 16, align: 'center', font: { size: 9,  bold: true, italic: true }, color: 'FFAAAAAA' },
      { key: 'deals',     header: 'Volume Discount',  width: 46, align: 'center', wrap: true }
    ];

    setLoading(btnEl, true);
    loadExcelJs()
      .then(function () {
        return Promise.all(items.map(function (item) { return fetchImg(item.imageUrl); }));
      })
      .then(function (imgs) {
        return buildXLSX({
          items         : items,
          columns       : columns,
          imageDataList : imgs,
          filename      : 'DFW-Reorder-' + dateStamp() + '.xlsx',
          sheetName     : 'Reorder',
          sheetTitle    : 'Reorder List',
          showTotals    : false,
          note          : 'Last Order Qty reflects your most recent order. Contact us to place a new order.'
        });
      })
      .then(function () { setLoading(btnEl, false); })
      .catch(function (err) {
        setLoading(btnEl, false);
        alert('Export failed: ' + (err && err.message || 'Unknown error'));
      });
  }

  /* =========================================================
     Auto-bind all 4 buttons on DOMContentLoaded
     ========================================================= */

  /* Use event delegation on document for all buttons.
     This survives Shopify AJAX cart re-renders — when the cart
     updates quantity it replaces the DOM, killing any listener
     bound directly to the button element. Delegating to document
     means the listener is always alive regardless of re-renders. */

  document.addEventListener('click', function (e) {

    /* Walk up from the clicked element to find a matching button */
    var target = e.target;
    while (target && target !== document) {

      /* Cart */
      if (target.hasAttribute && target.hasAttribute('data-dfw-export-btn')) {
        e.preventDefault();
        runCart(target);
        return;
      }

      /* Express Order page */
      if (target.id === 'dfw-download-express') {
        e.preventDefault();
        runExpress(target);
        return;
      }

      /* Collection page */
      if (target.id === 'dfw-download-collection') {
        e.preventDefault();
        runExpress(target);
        return;
      }

      /* Reorder page */
      if (target.id === 'dfw-download-reorder') {
        e.preventDefault();
        runReorder(target);
        return;
      }

      target = target.parentElement;
    }

  });

/* =========================================================
     ╔════════════════════════════════════════════════════════╗
     ║ PHASE 1 ADDITION — runQuoteFile()                      ║
     ║                                                        ║
     ║ Returns a quote Excel as a Blob instead of downloading.║
     ║ Used by dfw-quote-submit.js (Phase 3).                 ║
     ║                                                        ║
     ║ Layout: same DFW header rows 1-3, then consolidated    ║
     ║ QUOTE FOR banner with date, address block, notes,      ║
     ║ a BXGY DEAL SUMMARY banner (when any tier is earned),  ║
     ║ then the standard cart product table with mint-green   ║
     ║ row highlighting on any line that qualified.           ║
     ║                                                        ║
     ║ Does NOT touch any existing function above. All 4      ║
     ║ download buttons keep working identically.             ║
     ╚════════════════════════════════════════════════════════╝
     ========================================================= */
 
  /* --------------------------------------------------------
     Build the quote Excel from live cart + customer info
     --------------------------------------------------------
     customerInfo: {
       hotel_name, contact_name, street, city, state, zip,
       email, phone, notes
     }
     quoteId: 'DFW-Q-YYYYMMDD-HHMM'
 
     Returns: Promise resolving to {
       file:        Blob,
       filename:    string,
       contentType: string
     }
     -------------------------------------------------------- */
  function runQuoteFile(customerInfo, quoteId) {
    customerInfo = customerInfo || {};
    quoteId = quoteId || generateQuoteId();
 
    /* Step 1 — gather BXGY map from cart DOM (same as runCart) */
    var bxgyMap = {};
    document.querySelectorAll('[data-item-row]').forEach(function (row) {
      var pid = row.dataset.productId || '';
      if (pid && row.dataset.bxgy) bxgyMap[pid] = row.dataset.bxgy;
    });
 
    if (!window.DFWBXGY_REGISTRY && window.DFWBXGY_REGISTRY_API) {
      window.DFWBXGY_REGISTRY_API.rebuild();
    }
 
    /* Step 2 — fetch live cart */
    return fetch('/cart.js')
      .then(function (res) {
        if (!res.ok) throw new Error('Could not load cart');
        return res.json();
      })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) {
          throw new Error('Cart is empty');
        }
 
        /* Step 3 — map cart items to row data.
           For each item we compute BOTH the display deal string
           AND the structured "earned tier" so the workbook can:
             (a) paint mint-green rows for qualifying lines
             (b) tally a summary banner up top
           buildDealsForItem is the shared helper used by runCart
           too, so the "✓ EARNED" formatting stays identical
           across the cart download and the quote download.      */
        var items = cart.items.map(function (item) {
          var pid = String(item.product_id);
          var imageUrl = '';
          if (item.image) {
            var imgBase = item.image.split('?')[0];
            imageUrl = (imgBase.startsWith('//') ? 'https:' + imgBase : imgBase) + '?width=100';
          }

          var bxgyInfo  = buildDealsForItem(pid, bxgyMap[pid] || '', item.quantity);
          var unitPrice = item.price / 100;

          return {
            productId:  pid,
            title:      item.product_title || item.title || '',
            sku:        item.sku || '',
            qty:        item.quantity,
            unitPrice:  unitPrice,
            linePrice:  item.line_price / 100,
            imageUrl:   imageUrl,
            url:        'https://gowithdfw.com' + (item.url || ''),
            deals:      bxgyInfo.dealsStr,
            /* BXGY computed fields used by buildQuoteWorkbook.
               freeQty already includes the multiplier so 50 boxes
               at Buy 25 → Get 2 correctly returns 4, not 2.        */
            earnedFree: bxgyInfo.freeQty,
            earnedTier: bxgyInfo.earned,
            multiplier: bxgyInfo.multiplier,
            freeValue:  bxgyInfo.freeQty * unitPrice
          };
        });
 
        /* Step 4 — load ExcelJS, fetch images, build workbook */
        return loadExcelJs()
          .then(function () {
            return Promise.all(items.map(function (item) { return fetchImg(item.imageUrl); }));
          })
          .then(function (imgs) {
            return buildQuoteWorkbook({
              items:         items,
              imageDataList: imgs,
              customerInfo:  customerInfo,
              quoteId:       quoteId,
              cart:          cart
            });
          });
      });
  }
 
  /* --------------------------------------------------------
     Build a workbook with the quote layout and return as Blob
     -------------------------------------------------------- */
  function buildQuoteWorkbook(opts) {
    var wb = new window.ExcelJS.Workbook();
    wb.creator = 'GoWithDFW.com';
    wb.created = new Date();
    wb.modified = new Date();
 
    var ws = wb.addWorksheet('Quote', {
      pageSetup: {
        paperSize:    9,
        orientation:  'landscape',
        fitToPage:    true,
        fitToWidth:   1,
        fitToHeight:  0
      }
    });
 
    /* Quote uses the same 7-column layout as cart.
       Only linePrice carries total:true so only Line Total is summed. */
    var columns = [
      { key: 'image',     header: 'Image',         width: 16, align: 'center' },
      { key: 'title',     header: 'Product Name',  width: 38, align: 'left'   },
      { key: 'sku',       header: 'SKU',           width: 20, align: 'left',
                                                   font: { size: 9 }, color: 'FF666666' },
      { key: 'qty',       header: 'Qty',           width: 8,  align: 'center',
                                                   bold: true, color: 'FF1A1A2E',
                                                   font: { size: 11, bold: true } },
      { key: 'unitPrice', header: 'Unit Price',    width: 13, align: 'right',
                                                   numFmt: '"$"#,##0.00' },
      { key: 'linePrice', header: 'Line Total',    width: 13, align: 'right',
                                                   numFmt: '"$"#,##0.00',
                                                   bold: true, color: 'FF1A1A2E',
                                                   total: true },
      { key: 'deals',     header: 'Volume Discount', width: 46, align: 'center', wrap: true }
    ];
 
    var N = columns.length;
    ws.columns = columns.map(function (c) { return { key: c.key, width: c.width }; });

    /* --------------------------------------------------
       BXGY tally — done up front so we know whether to
       insert the summary banner row before the headers.

       Also tallies subtotal so the gold SUBTOTAL pill in
       the Volume Discount banner can display it alongside
       the savings percentage.
       -------------------------------------------------- */
    var totalFreeBoxes = 0;
    var totalFreeValue = 0;
    var orderSubtotal  = 0;
    opts.items.forEach(function (it) {
      totalFreeBoxes += (it.earnedFree || 0);
      totalFreeValue += (it.freeValue  || 0);
      orderSubtotal  += (it.linePrice  || 0);
    });
    var hasBxgy    = totalFreeBoxes > 0;
    var savingsPct = (hasBxgy && orderSubtotal > 0)
                   ? (totalFreeValue / orderSubtotal) * 100
                   : 0;

    /* --------------------------------------------------
       Option E Header (rows 1-2 only — was 4 rows)
       --------------------------------------------------
       Row 1 — Navy title "DFW Motel Supply" + tagline below
       Row 2 — Cream contact bar: phones left, website+email right

       Trims 2 rows off the previous quote layout. The gold
       QUOTE FOR banner is gone; the "Generated:" timestamp now
       lives inside the contact block (right half of address row)
       under the Quote ID. */

    /* Row 1 — Title row with brand name (centered on navy).
       Tagline removed per Ankit's request. */
    ws.mergeCells(1, 1, 1, N);
    var titleCell = ws.getRow(1).getCell(1);
    titleCell.value = 'DFW Motel Supply';
    titleCell.font  = { name: 'Calibri', bold: true, size: 22, color: { argb: 'FFFFFFFF' } };
    titleCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A2E' } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    ws.getRow(1).height = 42;

    /* Row 2 — Cream contact bar split 60/40 */
    var creamSplit = Math.max(1, Math.floor(N * 0.6));

    if (creamSplit > 1) {
      ws.mergeCells(2, 1, 2, creamSplit);
    }
    var phonesCell = ws.getRow(2).getCell(1);
    phonesCell.value = {
      richText: [
        { text: 'TX (214) 745-8866   ',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF5A3E00' } } },
        { text: '|',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FFC07800' } } },
        { text: '   CA (951) 788-0000   ',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF5A3E00' } } },
        { text: '|',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FFC07800' } } },
        { text: '   NJ (856) 423-3400',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF5A3E00' } } }
      ]
    };
    phonesCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7E0' } };
    phonesCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    phonesCell.border = {
      top:    { style: 'thin', color: { argb: 'FFE8A000' } },
      bottom: { style: 'thin', color: { argb: 'FFE8A000' } }
    };

    if (creamSplit < N) {
      ws.mergeCells(2, creamSplit + 1, 2, N);
    }
    var webEmailCell = ws.getRow(2).getCell(creamSplit + 1);
    webEmailCell.value = {
      richText: [
        { text: 'www.gowithdfw.com  ',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF5A3E00' } } },
        { text: '\u00B7',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FFC07800' } } },
        { text: '  sales@dfwmotelsupply.com',
          font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF5A3E00' } } }
      ]
    };
    webEmailCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7E0' } };
    webEmailCell.alignment = { vertical: 'middle', horizontal: 'right', indent: 1 };
    webEmailCell.border = {
      top:    { style: 'thin', color: { argb: 'FFE8A000' } },
      bottom: { style: 'thin', color: { argb: 'FFE8A000' } }
    };

    ws.getRow(2).height = 22;
 
    /* --------------------------------------------------
       ROW 3 — Address block (2 merged halves)
       --------------------------------------------------
       Left half:  hotel address (QUOTE FOR label inside)
       Right half: contact + Quote ID + Generated timestamp
                   (timestamp moved here from the old gold
                    QUOTE FOR row, which Option E removed) */
    var half = Math.floor(N / 2);
 
    /* LEFT: stacked hotel address */
    ws.mergeCells(3, 1, 3, half);
    var addrCell = ws.getRow(3).getCell(1);
    var addrLines = buildAddressLines(opts.customerInfo);
    addrCell.value = {
      richText: addrLines
    };
    addrCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true, indent: 1 };
    addrCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
 
    /* RIGHT: contact + quote ID + generated timestamp */
    ws.mergeCells(3, half + 1, 3, N);
    var contactCell = ws.getRow(3).getCell(half + 1);
    var contactLines = buildContactLines(opts.customerInfo, opts.quoteId);
    contactCell.value = {
      richText: contactLines
    };
    contactCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true, indent: 1 };
    contactCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
 
    ws.getRow(3).height = 100;   /* slightly taller for extra Generated line */

    /* --------------------------------------------------
       ROW 4 — Notes (conditional)
       -------------------------------------------------- */
    var notesText = (opts.customerInfo.notes || '').trim();
    if (notesText) {
      ws.mergeCells(4, 1, 4, N);
      var notesCell = ws.getRow(4).getCell(1);
      notesCell.value = {
        richText: [
          { text: 'Notes:  ',
            font: { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF666666' } } },
          { text: notesText,
            font: { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF1A1A2E' } } }
        ]
      };
      notesCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFDFAF0' } };
      notesCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true, indent: 1 };
      notesCell.border = {
        bottom: { style: 'thin', color: { argb: 'FFEEEEEE' } }
      };
      ws.getRow(4).height = notesText.length > 100 ? 36 : 22;
    } else {
      ws.getRow(4).height = 6;
    }

    /* --------------------------------------------------
       ROW 5 (conditional) — VOLUME DISCOUNT BANNER
       --------------------------------------------------
       Three sections in one mint row:
         Left  (cols 1..N-2)    text: deal info
         Mid   (col N-1)        gold pill: "SUBTOTAL $X,XXX.XX"
         Right (col N)          green pill: "X.X% SAVED"

       The gold subtotal pill previews the gold ORDER TOTAL
       at the bottom — color bookends the file. */
    var bxgyRow = 0;
    if (hasBxgy) {
      bxgyRow = 5;

      /* --------------------------------------------------
         Banner column allocation (same as cart layout):
           cols 1 to N-3  → deal text (left, merged)
           cols N-2..N-1  → gold SUBTOTAL pill (merged)
           col N          → green % SAVED pill
         Two-cell merge for the subtotal pill prevents
         clipping on larger orders (was clipping at $20,990
         in a single cell). Number formatted with commas. */

      /* Left section — banner text */
      var bannerLeftEnd = Math.max(1, N - 3);
      if (bannerLeftEnd > 1) {
        ws.mergeCells(bxgyRow, 1, bxgyRow, bannerLeftEnd);
      }
      var bxgyCell = ws.getRow(bxgyRow).getCell(1);
      bxgyCell.value = {
        richText: [
          { text: '\uD83C\uDF81  VOLUME DISCOUNT APPLIED:   ',
            font: { name: 'Calibri', bold: true, size: 12, color: { argb: 'FF1F6B2C' } } },
          { text: totalFreeBoxes + (totalFreeBoxes === 1 ? ' free case' : ' free cases'),
            font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF1A1A2E' } } },
          { text: '      |      Value:  ',
            font: { name: 'Calibri', size: 11, color: { argb: 'FF1F6B2C' } } },
          { text: formatMoney(totalFreeValue),
            font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF1A1A2E' } } }
        ]
      };
      bxgyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD4F4DD' } };
      bxgyCell.alignment = { vertical: 'middle', horizontal: 'center' };
      bxgyCell.border = {
        top:    { style: 'medium', color: { argb: 'FF6FBF7C' } },
        bottom: { style: 'medium', color: { argb: 'FF6FBF7C' } }
      };

      /* Middle — gold SUBTOTAL pill, merged across 2 cells */
      if (N >= 3) {
        ws.mergeCells(bxgyRow, N - 2, bxgyRow, N - 1);
      }
      var subtotalPill = ws.getRow(bxgyRow).getCell(Math.max(1, N - 2));
      subtotalPill.value     = 'SUBTOTAL  ' + formatMoney(orderSubtotal);
      subtotalPill.font      = { name: 'Calibri', bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      subtotalPill.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8A000' } };
      subtotalPill.alignment = { vertical: 'middle', horizontal: 'center' };
      subtotalPill.border = {
        top:    { style: 'medium', color: { argb: 'FF6FBF7C' } },
        bottom: { style: 'medium', color: { argb: 'FF6FBF7C' } }
      };

      /* Right — green % SAVED pill */
      var savedPill = ws.getRow(bxgyRow).getCell(N);
      savedPill.value     = savingsPct.toFixed(1) + '% SAVED';
      savedPill.font      = { name: 'Calibri', bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      savedPill.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F6B2C' } };
      savedPill.alignment = { vertical: 'middle', horizontal: 'center' };
      savedPill.border = {
        top:    { style: 'medium', color: { argb: 'FF6FBF7C' } },
        bottom: { style: 'medium', color: { argb: 'FF6FBF7C' } }
      };

      ws.getRow(bxgyRow).height = 28;
    }

    /* --------------------------------------------------
       Column headers (FROZEN) — row depends on banner presence
       --------------------------------------------------
       With BXGY:    row 6 (banner on row 5)
       Without BXGY: row 5 (no banner, skip straight from notes)
       Quote layout: rows 1-2 = Option E header, row 3 = address,
       row 4 = notes, row 5 = banner OR column headers. */
    var HEADER_ROW = hasBxgy ? 6 : 5;
    columns.forEach(function (col, i) {
      var cell = ws.getRow(HEADER_ROW).getCell(i + 1);
      cell.value = col.header;
      cell.font = { name: 'Calibri', bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2D2D4E' } };
      cell.alignment = { vertical: 'middle', horizontal: col.align || 'left' };
    });
    ws.getRow(HEADER_ROW).height = 22;
 
    /* --------------------------------------------------
       Product rows — start right after the header
       -------------------------------------------------- */
    var DATA_START = HEADER_ROW + 1;
    var ROW_HEIGHT = 50;
    var IMG_PX = 50;
    var imgCol = imgColIndex(columns);
    var totals = {};
    var currentRow = DATA_START;
    var stripeIndex = 0;   /* only advances on non-mint rows so stripes alternate cleanly */
 
    opts.items.forEach(function (item, idx) {
      var rowNum = currentRow;
      currentRow++;
 
      var row = ws.getRow(rowNum);
      row.height = ROW_HEIGHT;

      /* BXGY: lines that earned a tier get mint-green background.
         Non-earning lines fall back to the standard alternating
         gray/white stripe. We only advance stripeIndex on non-mint
         rows so the stripe pattern reads cleanly when mint rows
         are interleaved. */
      var earnedThisRow = (item.earnedFree || 0) > 0;
      var bg;
      if (earnedThisRow) {
        bg = 'FFD4F4DD';   /* mint */
      } else {
        bg = stripeIndex % 2 === 0 ? 'FFFAFAFA' : 'FFFFFFFF';
        stripeIndex++;
      }
 
      columns.forEach(function (col, colIdx) {
        var c = row.getCell(colIdx + 1);
        var val = item[col.key];
 
        var font = Object.assign({ name: 'Calibri', size: 10 }, col.font || {});
        if (col.bold)  font.bold = true;
        if (col.color) font.color = { argb: col.color };
 
        if (col.key === 'image') {
          c.value = '';
        } else if (col.key === 'title') {
          c.value = item.url
            ? { text: String(val || ''), hyperlink: item.url }
            : String(val || '');
          font.bold = true;
          font.color = { argb: 'FF1A1A2E' };
        } else if (col.key === 'deals') {
          c.value = val || '';
          font.size = 9;
          /* On earned rows, the deals text becomes bold dark green
             so the "✓ EARNED" label pops against the mint background.
             On non-earned rows, keep the existing brown-orange tier
             list color. */
          if (earnedThisRow) {
            font.bold  = true;
            font.color = { argb: 'FF1F6B2C' };
          } else {
            font.color = { argb: val ? 'FF9A6000' : 'FFAAAAAA' };
          }
        } else if (col.numFmt) {
          var num = (typeof val === 'number') ? val : cleanPrice(val);
          c.value = num;
          c.numFmt = col.numFmt;
          /* Only sum columns marked total:true so we don't sum Unit Price */
          if (col.total) {
            totals[col.key] = (totals[col.key] || 0) + num;
          }
        } else {
          c.value = (val !== undefined && val !== null) ? val : '';
        }
 
        c.font = font;
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
        c.border = { bottom: { style: 'thin', color: { argb: 'FFE5E5E5' } } };
        c.alignment = {
          vertical:   'middle',
          horizontal: col.align || 'left',
          wrapText:   col.wrap || false
        };
        if (col.numFmt && col.key !== 'deals') c.numFmt = col.numFmt;
      });
 
      /* Embed product image */
      var b64 = opts.imageDataList && opts.imageDataList[idx];
      if (b64 && imgCol >= 0) {
        try {
          var imgId = wb.addImage({ base64: b64, extension: 'jpeg' });
          ws.addImage(imgId, {
            tl:     { col: imgCol, row: rowNum - 1, nativeColOff: 90000 * 2, nativeRowOff: 90000 * 2 },
            ext:    { width: IMG_PX, height: IMG_PX },
            editAs: 'oneCell'
          });
        } catch (e) {}
      }
    });
 
    /* --------------------------------------------------
       ORDER TOTAL row
       -------------------------------------------------- */
    var totalRow = currentRow;
    ws.getRow(totalRow).height = 26;
    var labelEnd = firstNumericColIndex(columns);
 
    if (labelEnd > 0) {
      ws.mergeCells(totalRow, 1, totalRow, labelEnd);
    }
    var tlLabel = ws.getRow(totalRow).getCell(1);
    tlLabel.value = 'ORDER TOTAL';
    tlLabel.font = { name: 'Calibri', bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
    tlLabel.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8A000' } };
    tlLabel.alignment = { vertical: 'middle', horizontal: 'right' };
 
    columns.forEach(function (col, colIdx) {
      if (colIdx < labelEnd) return;
      var tc = ws.getRow(totalRow).getCell(colIdx + 1);
      if (totals[col.key] !== undefined) {
        tc.value = totals[col.key];
        tc.numFmt = col.numFmt;
      } else {
        tc.value = '';
      }
      tc.font = { name: 'Calibri', bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      tc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8A000' } };
      tc.alignment = { vertical: 'middle', horizontal: col.align || 'right' };
    });
 
    /* --------------------------------------------------
       Footer note row
       -------------------------------------------------- */
    var noteRow = totalRow + 1;
    ws.mergeCells(noteRow, 1, noteRow, N);
    var noteCell = ws.getRow(noteRow).getCell(1);
    noteCell.value = 'Volume discount free cases are processed at order confirmation. Prices shown may exclude applied discounts. Quote valid for 30 days.';
    noteCell.font = { name: 'Calibri', size: 8, italic: true, color: { argb: 'FF999999' } };
    noteCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(noteRow).height = 16;
 
    /* Freeze the column header row */
    ws.views = [{ state: 'frozen', ySplit: HEADER_ROW, activeCell: 'B' + DATA_START }];
 
    /* --------------------------------------------------
       Generate Blob and return — DO NOT download
       -------------------------------------------------- */
    return wb.xlsx.writeBuffer().then(function (buffer) {
      var blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      return {
        file:        blob,
        filename:    'DFW-Quote-' + opts.quoteId + '.xlsx',
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      };
    });
  }
 
  /* --------------------------------------------------------
     Build the stacked address rich text for the left half.

     Starts with a tiny "QUOTE FOR" uppercase label that
     mirrors the "CONTACT" label on the right side. Replaces
     the gold QUOTE FOR banner row that Option E removed.
     -------------------------------------------------------- */
  function buildAddressLines(info) {
    var lines = [];

    /* Tiny "QUOTE FOR" label so the reader knows what this
       block is. Matches the "CONTACT" label on the right. */
    lines.push({
      text: 'QUOTE FOR\n',
      font: { name: 'Calibri', bold: true, size: 9, color: { argb: 'FF666666' } }
    });
 
    var hotelName = (info.hotel_name || '').trim();
    if (hotelName) {
      lines.push({
        text: hotelName + '\n',
        font: { name: 'Calibri', bold: true, size: 13, color: { argb: 'FF1A1A2E' } }
      });
    }
 
    var contactName = (info.contact_name || '').trim();
    if (contactName) {
      lines.push({
        text: contactName + '\n',
        font: { name: 'Calibri', size: 11, color: { argb: 'FF1A1A2E' } }
      });
    }
 
    var street = (info.street || '').trim();
    if (street) {
      lines.push({
        text: street + '\n',
        font: { name: 'Calibri', size: 11, color: { argb: 'FF1A1A2E' } }
      });
    }
 
    /* City, State ZIP */
    var city = (info.city || '').trim();
    var state = (info.state || '').trim();
    var zip = (info.zip || '').trim();
    var cityLine = '';
    if (city && state) cityLine = city + ', ' + state;
    else if (city)     cityLine = city;
    else if (state)    cityLine = state;
    if (zip) cityLine = cityLine ? cityLine + '  ' + zip : zip;
    if (cityLine) {
      lines.push({
        text: cityLine,
        font: { name: 'Calibri', size: 11, color: { argb: 'FF1A1A2E' } }
      });
    }
 
    if (!lines.length) {
      lines.push({ text: '(No address provided)',
        font: { name: 'Calibri', italic: true, size: 10, color: { argb: 'FFAAAAAA' } } });
    }
 
    return lines;
  }
 
  /* --------------------------------------------------------
     Build the contact + Quote ID + Generated timestamp
     rich text for the right half of the address row.

     The Generated timestamp used to live in the gold QUOTE FOR
     row (row 4 in the old layout). That row is gone with Option E,
     so the timestamp now sits at the bottom of this contact block
     under the Quote ID — a natural home since both are
     identifying metadata for the document itself.
     -------------------------------------------------------- */
  function buildContactLines(info, quoteId) {
    var lines = [];
 
    lines.push({
      text: 'CONTACT\n',
      font: { name: 'Calibri', bold: true, size: 9, color: { argb: 'FF666666' } }
    });
 
    var email = (info.email || '').trim();
    if (email) {
      lines.push({
        text: 'Email:  ',
        font: { name: 'Calibri', size: 10, color: { argb: 'FF888888' } }
      });
      lines.push({
        text: email + '\n',
        font: { name: 'Calibri', size: 11, color: { argb: 'FF1A1A2E' } }
      });
    }
 
    var phone = (info.phone || '').trim();
    if (phone) {
      lines.push({
        text: 'Phone:  ',
        font: { name: 'Calibri', size: 10, color: { argb: 'FF888888' } }
      });
      lines.push({
        text: phone + '\n',
        font: { name: 'Calibri', size: 11, color: { argb: 'FF1A1A2E' } }
      });
    }
 
    if (quoteId) {
      lines.push({
        text: '\nQuote ID:  ',
        font: { name: 'Calibri', size: 10, color: { argb: 'FF888888' } }
      });
      lines.push({
        text: quoteId + '\n',
        font: { name: 'Consolas', size: 10, color: { argb: 'FF555555' } }
      });
    }

    /* Generated timestamp — moved here from the old gold
       QUOTE FOR row when Option E layout removed that row.
       Appears as the last line of the contact block. */
    lines.push({
      text: 'Generated:  ',
      font: { name: 'Calibri', size: 10, color: { argb: 'FF888888' } }
    });
    lines.push({
      text: new Date().toLocaleString(),
      font: { name: 'Consolas', size: 10, color: { argb: 'FF555555' } }
    });
 
    return lines;
  }
 
  /* --------------------------------------------------------
     Generate a quote ID from current date/time
     Format: DFW-Q-YYYYMMDD-HHMM
     -------------------------------------------------------- */
  function generateQuoteId() {
    var d = new Date();
    var pad = function (n) { return String(n).padStart(2, '0'); };
    return 'DFW-Q-' +
      d.getFullYear() +
      pad(d.getMonth() + 1) +
      pad(d.getDate()) +
      '-' +
      pad(d.getHours()) +
      pad(d.getMinutes());
  }
 
  /* =========================================================
     Public API
     ========================================================= */
  window.dfwXLSX = {
    runCart:           runCart,
    runExpress:        runExpress,
    runReorder:        runReorder,
    runQuoteFile:      runQuoteFile,    /* Phase 1 */
    buildXLSX:         buildXLSX,
    generateQuoteId:   generateQuoteId, /* Phase 1 helper */
    /* Exposed for debugging / future surfaces that need to
       reason about BXGY tiers without recreating the parse */
    getDealsInfo:      getDealsInfo,
    getEarnedTier:     getEarnedTier,
    buildDealsForItem: buildDealsForItem
  };
 
})();
/* =========================================================
   Ankit END - DFW XLSX Export (Unified)
   ========================================================= */